import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageTransition from '../components/Motion/PageTransition';

// Import BI Tool Icons
import PowerBIDashboardIcon from '../icons/Business Intelligence & Analytics/PowerBIDashboard.png';
import FabricIcon from '../icons/Business Intelligence & Analytics/fabric_48_color.svg';
import AzureIcon from '../icons/Business Intelligence & Analytics/azure.svg';
import AzureSynapseIcon from '../icons/Business Intelligence & Analytics/AzureSynapse.png';
import SQLServerIcon from '../icons/Business Intelligence & Analytics/SQLServer&SSRS.png';
import PowerAutomateIcon from '../icons/Business Intelligence & Analytics/PowerAutomate.png';

/**
 * BI Tools Page - Detailed overview of Business Intelligence tools and solutions
 * Showcases Power BI, Excel, and other analytics tools with official branding
 */
function BITools() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const biTools = [
    {
      id: 'power-bi-dashboards',
      name: 'Power BI Dashboards',
      icon: PowerBIDashboardIcon,
      description: 'Create stunning, interactive dashboards that bring your data to life. Our Power BI dashboards provide real-time insights with drill-down capabilities, custom visualizations, and seamless integration with your data sources.',
      features: [
        'Real-time data refresh and monitoring',
        'Interactive filters and cross-filtering',
        'Mobile-optimized responsive design',
        'Custom DAX calculations and measures',
        'Row-level security for data governance',
        'Scheduled report distribution'
      ],
      benefits: 'Empower every stakeholder with self-service analytics. Make faster decisions with live data at your fingertips.',
      useCases: 'Financial KPIs, Sales Performance, Warehouse Operations, Executive Dashboards'
    },
    {
      id: 'fabric-integration',
      name: 'Microsoft Fabric',
      icon: FabricIcon,
      description: 'Unify your entire analytics estate with Microsoft Fabric\'s all-in-one SaaS platform. We implement end-to-end solutions leveraging OneLake, Data Factory, Synapse, and Power BI in a single integrated environment with built-in governance.',
      features: [
        'OneLake unified data lake foundation',
        'Data Factory pipelines for ETL/ELT',
        'Synapse Data Warehouse and Engineering',
        'Real-time analytics with KQL databases',
        'Native Power BI integration',
        'Centralized data governance and security',
        'Cross-workspace collaboration and sharing'
      ],
      benefits: 'Eliminate data silos and tool sprawl. Get enterprise analytics without complex integrations—everything works together seamlessly.',
      useCases: 'Enterprise Data Warehousing, Real-time Dashboards, Unified Analytics, Multi-source Data Integration'
    },
    {
      id: 'azure',
      name: 'Microsoft Azure',
      icon: AzureIcon,
      description: 'Cloud computing platform offering comprehensive data and analytics services. We leverage Azure\'s ecosystem including Synapse Analytics, Data Factory, Databricks, and Azure SQL to build scalable, secure, and intelligent data solutions for modern enterprises.',
      features: [
        'Scalable cloud infrastructure and services',
        'Integrated data analytics and AI capabilities',
        'Enterprise-grade security and compliance',
        'Hybrid and multi-cloud connectivity',
        'Pay-as-you-go pricing model',
        'Global datacenter presence'
      ],
      benefits: 'Leverage Microsoft\'s cloud platform to build, deploy, and manage applications and data solutions with flexibility and enterprise-level reliability.',
      useCases: 'Cloud Data Warehousing, Real-time Analytics, Machine Learning, Data Integration, Business Intelligence'
    },
    {
      id: 'azure-synapse',
      name: 'Azure Synapse Analytics',
      icon: AzureSynapseIcon,
      description: 'Enterprise-grade data warehousing and big data analytics platform. We design and implement Synapse solutions for companies dealing with massive datasets requiring advanced analytics, machine learning, and real-time processing.',
      features: [
        'Unlimited analytics with on-demand resources',
        'Unified data integration and transformation',
        'Apache Spark and SQL analytics engines',
        'Built-in machine learning capabilities',
        'Enterprise security and compliance',
        'Seamless Power BI integration'
      ],
      benefits: 'Scale your analytics infrastructure as you grow. Handle billions of rows without performance degradation.',
      useCases: 'Data Warehousing, Big Data Analytics, Predictive Modeling, IoT Analytics'
    },
    {
      id: 'sql-server',
      name: 'SQL Server & SSRS',
      icon: SQLServerIcon,
      description: 'Robust database management and reporting services for mission-critical business applications. We develop secure, high-performance SQL databases with automated backup, optimization, and SQL Server Reporting Services (SSRS) integration.',
      features: [
        'High-performance database design',
        'Stored procedures and functions',
        'SSRS paginated report development',
        'Automated backup and recovery',
        'Query optimization and indexing',
        'Role-based access control'
      ],
      benefits: 'Rock-solid data foundation for your business. Proven enterprise reliability with 99.9% uptime.',
      useCases: 'Transactional Systems, Data Warehouses, Operational Reports, Audit Trails'
    },
    {
      id: 'power-automate',
      name: 'Power Automate',
      icon: PowerAutomateIcon,
      description: 'Automate repetitive tasks and create seamless workflows between your business applications. From automated data refresh to email notifications and approval processes, Power Automate streamlines your operations.',
      features: [
        'Scheduled and triggered workflows',
        'Email notifications and alerts',
        'Data synchronization between systems',
        'Approval workflow automation',
        'Integration with 500+ connectors',
        'Error handling and logging'
      ],
      benefits: 'Eliminate manual tasks and reduce errors. Free up your team to focus on strategic work instead of data entry.',
      useCases: 'Automated Reporting, Data Backup, Alert Systems, Approval Workflows'
    }
  ];

  return (
    <PageTransition>
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mb-16"
            initial="hidden"
            animate="visible"
            variants={headerVariants}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our BI Tools & Solutions
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              We leverage industry-leading business intelligence tools to transform your data into actionable insights.
              Our expertise spans the entire Microsoft Power Platform ecosystem and beyond.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-50 rounded-full text-blue-700 font-semibold">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Microsoft Gold Partner Certified
            </div>
          </motion.div>

          {/* Tools Grid */}
          <motion.div
            className="max-w-7xl mx-auto space-y-8"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {biTools.map((tool) => (
              <motion.div
                key={tool.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                variants={cardVariants}
                whileHover={{ y: -4 }}
              >
                <div className="grid md:grid-cols-3 gap-6 p-8">
                  {/* Icon and Name */}
                  <div className="md:col-span-1 flex flex-col items-center md:items-start text-center md:text-left">
                    <div className="w-24 h-24 mb-4 flex items-center justify-center">
                      <img
                        src={tool.icon}
                        alt={`${tool.name} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{tool.name}</h2>
                  </div>

                  {/* Description and Details */}
                  <div className="md:col-span-2">
                    <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                      {tool.description}
                    </p>

                    {/* Features Grid */}
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                        Key Features
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {tool.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Benefits */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-600 mb-4">
                      <p className="text-sm font-semibold text-gray-800">
                        <span className="text-blue-700">Why it matters:</span> {tool.benefits}
                      </p>
                    </div>

                    {/* Use Cases */}
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Common Use Cases:</span>
                      {tool.useCases.split(', ').map((useCase, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mt-16 p-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4">Ready to Harness the Power of BI?</h2>
            <p className="text-blue-100 mb-6 text-lg">
              Let's discuss which tools and solutions are the perfect fit for your business needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
              >
                Schedule a Consultation
              </Link>
              <Link
                to="/projects"
                className="inline-block bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors border-2 border-white"
              >
                View Our Projects
              </Link>
            </div>
          </motion.div>

          {/* Back to Services */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold transition-colors group"
            >
              <svg
                className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Services
            </Link>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

export default BITools;

